import {
  Building2,
  CheckCircle2,
  Clock,
  Coins,
  DollarSign,
  RefreshCw,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { services } from '../../services';
import { Button } from '../ui/button';
import {
  ActivityFeed,
  AlertCenter,
  AssetPerformanceChart,
  HedgeFundMetrics,
  KPICard,
  LiveTicker,
  PortfolioAllocation,
  QuickActions,
  TopInvestors,
} from './index';

interface KPIData {
  totalUsers: { value: string; change: number; trend: 'up' | 'down' | 'neutral' };
  totalAssets: { value: string; change: number; trend: 'up' | 'down' | 'neutral' };
  totalTransactions: { value: string; change: number; trend: 'up' | 'down' | 'neutral' };
  totalInvestmentValue: { value: string; change: number; trend: 'up' | 'down' | 'neutral' };
  pendingApprovals?: { value: string; change?: number; trend: 'up' | 'down' | 'neutral' };
  yieldDistributed?: { value: string; change: number; trend: 'up' | 'down' | 'neutral' };
}

export function Dashboard() {
  const [kpiData, setKpiData] = useState<KPIData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshingPrice, setRefreshingPrice] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchKPIs();
    checkAdminAccess();
  }, []);

  const checkAdminAccess = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const userRole = typeof user.role === 'object' ? user.role?.roleName : user.role;
        setIsAdmin(userRole === 'ADMIN');
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
    }
  };

  const fetchKPIs = async () => {
    try {
      setLoading(true);
      const response = await services.get<{
        status: boolean;
        message?: string;
        data: KPIData;
      }>('/admin/dashboard/kpis');

      if (response.status === 200 && response.data?.status && response.data?.data) {
        setKpiData(response.data.data);
        setLastUpdated(new Date());
      } else if (response.status === 401) {
        console.error('Authentication required for dashboard KPIs');
      } else if (response.status === 403) {
        console.error('Admin access required for dashboard KPIs');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required for dashboard KPIs');
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required for dashboard KPIs');
      } else {
        console.error('Error fetching dashboard KPIs:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshPrice = async () => {
    try {
      setRefreshingPrice(true);
      await services.refreshEthPrice();
      // Refresh KPIs after price update
      await fetchKPIs();
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required for price refresh');
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required for price refresh');
      } else {
        console.error('Error refreshing price:', error);
      }
    } finally {
      setRefreshingPrice(false);
    }
  };

  const formatTransactionCount = (value: string): string => {
    const num = parseInt(value);
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return value;
  };

  const formatCompactCurrency = (value: string): string => {
    const num = parseFloat(value.replace(/[$,]/g, ''));
    if (isNaN(num)) return value;
    if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(1)}B`;
    if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`;
    return value;
  };

  return (
    <div className='space-y-6'>
      {/* Live Ticker */}
      <LiveTicker />

      {/* Page Header */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-xl sm:text-2xl font-bold text-foreground'>Executive Dashboard</h1>
          <p className='text-muted-foreground text-sm sm:text-base'>
            Real-time portfolio performance and analytics.
          </p>
        </div>
        <div className='flex items-center gap-2 sm:gap-3 flex-wrap'>
          <div className='flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-success/10 border border-success/20'>
            <span className='w-2 h-2 rounded-full bg-success animate-pulse' />
            <span className='text-xs font-medium text-success'>Markets Open</span>
          </div>
          {lastUpdated && (
            <div className='flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20'>
              <CheckCircle2 className='w-3 h-3 text-blue-500' />
              <span className='text-xs font-medium text-blue-600 dark:text-blue-400'>
                Prices updated {lastUpdated.toLocaleTimeString()}
              </span>
            </div>
          )}
          {isAdmin && (
            <Button
              variant='outline'
              size='sm'
              onClick={handleRefreshPrice}
              disabled={refreshingPrice}
              className='flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-1.5 h-auto text-xs'
            >
              <RefreshCw className={`w-3 h-3 ${refreshingPrice ? 'animate-spin' : ''}`} />
              {refreshingPrice ? 'Refreshing...' : 'Refresh Price'}
            </Button>
          )}
          <div className='text-xs sm:text-sm text-muted-foreground'>
            {new Date().toLocaleString()}
          </div>
        </div>
      </div>

      {/* Fund Metrics */}
      <HedgeFundMetrics />

      {/* KPI Grid */}
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3'>
        {loading ? (
          <>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className='glass-card rounded-xl p-6 animate-pulse h-32' />
            ))}
          </>
        ) : kpiData ? (
          <>
            <KPICard
              title='Total AUM'
              value={formatCompactCurrency(kpiData.totalInvestmentValue.value)}
              change={kpiData.totalInvestmentValue.change}
              changeLabel='vs last month'
              icon={<DollarSign className='w-6 h-6' />}
              trend={kpiData.totalInvestmentValue.trend}
              className='xl:col-span-1'
            />
            <KPICard
              title='Active Users'
              value={kpiData.totalUsers.value}
              change={kpiData.totalUsers.change}
              changeLabel='vs last month'
              icon={<Users className='w-6 h-6' />}
              trend={kpiData.totalUsers.trend}
              className='xl:col-span-1'
            />
            <KPICard
              title='Pending Approvals'
              value={kpiData.pendingApprovals?.value || '0'}
              icon={<Clock className='w-6 h-6' />}
              trend='neutral'
              className='xl:col-span-1'
            />
            <KPICard
              title='Total Assets'
              value={kpiData.totalAssets.value}
              change={kpiData.totalAssets.change}
              changeLabel='this quarter'
              icon={<Building2 className='w-6 h-6' />}
              trend={kpiData.totalAssets.trend}
              className='xl:col-span-1'
            />
            <KPICard
              title='Total Transactions'
              value={formatTransactionCount(kpiData.totalTransactions.value)}
              change={kpiData.totalTransactions.change}
              changeLabel='vs last month'
              icon={<TrendingUp className='w-6 h-6' />}
              trend={kpiData.totalTransactions.trend}
              className='xl:col-span-1'
            />
            <KPICard
              title='Yield Distributed'
              value={kpiData.yieldDistributed?.value || '$0.0M'}
              change={kpiData.yieldDistributed?.change}
              changeLabel='this quarter'
              icon={<Coins className='w-6 h-6' />}
              trend={kpiData.yieldDistributed?.trend || 'neutral'}
              className='xl:col-span-1'
            />
          </>
        ) : null}
      </div>

      {/* Charts Row */}
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        <AssetPerformanceChart className='lg:col-span-2' />
        <PortfolioAllocation />
      </div>

      {/* Main Content Grid */}
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        <div className='lg:col-span-2 space-y-6'>
          <ActivityFeed />
          <AlertCenter />
        </div>
        <div className='space-y-6'>
          <QuickActions />
          <TopInvestors />
        </div>
      </div>
    </div>
  );
}
